import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/clients/ClientsPage';
import SellersPage from './pages/sellers/SellersPage';
import CheckInsMapPage from './pages/checkins/CheckInsMapPage';
import HistoryPage from './pages/checkins/HistoryPage';
import ReportsPage from './pages/reports/ReportsPage';
import SettingsPage from './pages/settings/SettingsPage';
import SmartRoutePage from './pages/route/SmartRoutePage';
import RotasPage from './pages/rotas/RotasPage';
import LiveMapPage from './pages/live/LiveMapPage';
import ReplayPage from './pages/replay/ReplayPage';
import WorkdayReplayPage from './pages/workday/WorkdayReplayPage';

// access: undefined = qualquer logado; 'staff' = full + gestor (não vendedor puro);
// 'full' = acesso total (admin/diretor/pricing/gerente_nacional); 'admin' = SÓ o papel literal `admin`.
function PrivateRoute({ children, access }: { children: React.ReactNode; access?: 'staff' | 'full' | 'admin' }) {
  const { user, isAdmin, isStaff, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (access === 'admin' && user.role !== 'admin') return <Navigate to="/clients" replace />;
  if (access === 'full' && !isAdmin) return <Navigate to="/clients" replace />;
  if (access === 'staff' && !isStaff) return <Navigate to="/clients" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, isStaff, loading } = useAuth();
  if (loading) return null;

  const home = isStaff ? '/dashboard' : '/clients';

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={home} replace /> : <LoginPage />} />
      <Route path="/dashboard" element={<PrivateRoute access="staff"><DashboardPage /></PrivateRoute>} />
      <Route path="/clients" element={<PrivateRoute><ClientsPage /></PrivateRoute>} />
      {/* Vendedores é EXCLUSIVA do papel literal `admin` (gestão de contas/senhas) */}
      <Route path="/sellers" element={<PrivateRoute access="admin"><SellersPage /></PrivateRoute>} />
      <Route path="/checkins" element={<PrivateRoute access="staff"><CheckInsMapPage /></PrivateRoute>} />
      <Route path="/live" element={<PrivateRoute access="staff"><LiveMapPage /></PrivateRoute>} />
      <Route path="/replay" element={<PrivateRoute access="staff"><ReplayPage /></PrivateRoute>} />
      {/* Replay do dia de TRABALHO (expediente): SÓ acesso total (admin/diretor/pricing/gerente_nacional) */}
      <Route path="/jornada" element={<PrivateRoute access="full"><WorkdayReplayPage /></PrivateRoute>} />
      <Route path="/history" element={<PrivateRoute><HistoryPage /></PrivateRoute>} />
      <Route path="/roteirizacao" element={<PrivateRoute><SmartRoutePage /></PrivateRoute>} />
      {/* "Rotas" é de GESTÃO: o gestor confere o roteiro de quem está abaixo dele. Vendedor não entra —
          não tem ninguém para validar e, pela regra `users.list`, nem consegue montar o seletor. */}
      <Route path="/rotas" element={<PrivateRoute access="staff"><RotasPage /></PrivateRoute>} />
      <Route path="/route" element={<Navigate to="/roteirizacao" replace />} />
      <Route path="/reports" element={<PrivateRoute access="staff"><ReportsPage /></PrivateRoute>} />
      {/* Sem gate de papel: a própria SettingsPage decide o conteúdo — `admin` vê tudo; os demais
          papéis veem SOMENTE o botão "Sincronizar Clientes". */}
      <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
      <Route path="*" element={<Navigate to={user ? home : '/login'} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
